import type { DoubaoCharacterConfigView } from "@actale/contracts";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

export function DoubaoCharacterConfigPanel({ refreshToken = 0 }: { refreshToken?: number }): React.ReactNode {
  const [config, setConfig] = useState<DoubaoCharacterConfigView | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [timeoutMs, setTimeoutMs] = useState(60_000);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [testConfirmed, setTestConfirmed] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  function applyConfig(value: DoubaoCharacterConfigView): void {
    setConfig(value);
    setApiKey("");
    setTimeoutMs(value.timeoutMs);
    setTestConfirmed(false);
    setDeleteConfirmed(false);
  }
  const load = useCallback(async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try { applyConfig(await api.doubaoCharacterConfig()); } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取豆包角色扮演配置");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load, refreshToken]);

  async function perform(action: "save" | "test" | "delete"): Promise<void> {
    if (!config || submitting.current) return;
    if (action === "test" && (!testConfirmed || !config.updatedAt || apiKey.trim() || timeoutMs !== config.timeoutMs)) return;
    if (action === "delete" && (!deleteConfirmed || !config.updatedAt)) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    setTestConfirmed(false);
    try {
      if (action === "save") {
        applyConfig(await api.saveDoubaoCharacterConfig({
          timeoutMs, expectedUpdatedAt: config.updatedAt,
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        }));
        setNotice("豆包配置已加密保存，没有调用模型；当前故事导演和真实 AI 开关未改变。");
      } else if (action === "test") {
        const result = await api.testDoubaoCharacterConfig(config.updatedAt!);
        setNotice(`连接通过：完整请求用时 ${(result.latencyMs / 1000).toFixed(2)} 秒，Token 用量 ${result.totalTokens ?? "供应商未返回"}。这不是故事体验验收结果。`);
        applyConfig(await api.doubaoCharacterConfig());
      } else {
        await api.deleteDoubaoCharacterConfig(config.updatedAt!);
        applyConfig(await api.doubaoCharacterConfig());
        setNotice("仅删除了豆包角色扮演配置；原故事导演、语音和视频配置未改变。");
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "操作未确认成功，请刷新查看；不会自动重试");
      if (action === "test") {
        try { applyConfig(await api.doubaoCharacterConfig()); } catch { /* Keep the original failure visible. */ }
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  function save(event: FormEvent): void { event.preventDefault(); void perform("save"); }
  const dirty = Boolean(apiKey.trim()) || timeoutMs !== config?.timeoutMs;
  const running = config?.lastTestStatus === "RUNNING" && config.lastTestedAt
    ? Date.now() - Date.parse(config.lastTestedAt) < config.timeoutMs + 5_000 : false;

  return <section aria-label="豆包角色扮演配置">
    <div className="provider-section-heading">
      <div><strong>豆包角色扮演 · Doubao-Seed-Character</strong><small>用于《雾中的灯塔》内部试玩的可选角色测试；全局导演保持原模型，APP 不受影响。请在选角页明确选择豆包角色测试。</small></div>
    </div>
    {error ? <div className="error-notice" role="alert">{error}</div> : null}
    {notice ? <div className="director-notice" role="status">{notice}</div> : null}
    {!config ? <div className="state-card">{error ? <button className="secondary-button" onClick={() => void load()}>重新读取豆包配置</button> : "正在读取豆包配置…"}</div> :
      <div className="provider-config-grid">
        <form className="provider-config-form" onSubmit={save}>
          <header><div><span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} /><strong>{config.configured ? "豆包配置已保存" : "豆包尚未配置"}</strong></div>{config.apiKeyHint ? <span className="provider-key-hint">密钥 {config.apiKeyHint}</span> : null}</header>
          <div className="provider-form-grid">
            <label className="provider-wide-field"><span>角色扮演模型</span><input value={config.modelName} readOnly /></label>
            <label className="provider-wide-field"><span>火山方舟官方接口</span><input value={config.apiBaseUrl} readOnly /><small>独立保存方舟 API Key，不会自动取用视频或语音的密钥。</small></label>
            <label className="provider-wide-field"><span>豆包角色扮演 API Key{config.configured ? "（留空不更换）" : ""}</span><input type="password" autoComplete="new-password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} disabled={busy || running} required={!config.configured} minLength={8} maxLength={8192} placeholder={config.apiKeyHint ? `已加密保存 ${config.apiKeyHint}` : "粘贴火山方舟 API Key"} /><small>保存后清空输入框，接口和日志不返回密钥明文。</small></label>
            <label><span>豆包最长等待（秒）</span><input type="number" min={1} max={120} step={1} required value={timeoutMs / 1000} disabled={busy || running} onChange={(event) => setTimeoutMs(Number(event.target.value) * 1000)} /></label>
            <div><strong>连接检查方式</strong><p>关闭深度思考，只请求一条简短回复；不发送故事或玩家资料。</p></div>
          </div>
          {!config.encryptionReady ? <div className="provider-security-alert" role="alert">服务器加密尚未就绪，暂不能保存密钥。</div> : null}
          <footer><span>{config.updatedAt ? `最近更新：${formatUpdatedAt(config.updatedAt)}` : "仅保存，不产生模型费用"}</span><button className="primary-button compact" disabled={busy || running || !config.encryptionReady}>{busy ? "正在处理…" : "安全保存豆包配置"}</button></footer>
        </form>
        <aside className="provider-validation-panel">
          <header><strong>豆包连接检查</strong><span>{config.lastTestStatus === "SUCCESS" ? "已通过" : config.lastTestStatus === "FAILED" ? "失败" : config.lastTestStatus === "RUNNING" ? (running ? "检查中" : "结果待核实") : "未检查"}</span></header>
          <p>只检查已保存配置的接口连通性，不代表多人剧情、速度或玩家体验已通过。</p>
          {config.lastTestedAt ? <small>{formatUpdatedAt(config.lastTestedAt)}{config.lastTestLatencyMs !== null ? ` · ${(config.lastTestLatencyMs / 1000).toFixed(2)} 秒` : ""}</small> : null}
          {config.lastTestErrorCode ? <code>{config.lastTestErrorCode}</code> : null}
          {dirty ? <p>有未保存修改，请先保存后检查。</p> : null}
          <label className="provider-test-confirm"><input type="checkbox" checked={testConfirmed} disabled={busy || running || dirty || !config.configured} onChange={(event) => setTestConfirmed(event.target.checked)} /><span>确认向火山方舟发送一次最小请求，可能产生少量费用。失败不自动重试。</span></label>
          <button type="button" className="secondary-button" disabled={busy || running || dirty || !config.configured || !testConfirmed} onClick={() => void perform("test")}>检查豆包连接</button>
          <button type="button" className="text-button" disabled={busy} onClick={() => void load()}>刷新豆包状态</button>
          {config.configured ? <div className="provider-delete-zone"><small>删除后豆包测试局将无法继续；原故事导演、语音和视频配置不变。</small>{!deleteConfirmed ? <button className="danger-text-button" disabled={busy || running} onClick={() => setDeleteConfirmed(true)}>删除豆包配置</button> : <><button className="danger-button" disabled={busy || running} onClick={() => void perform("delete")}>确认删除豆包配置</button><button className="text-button" disabled={busy} onClick={() => setDeleteConfirmed(false)}>取消</button></>}</div> : null}
        </aside>
      </div>}
  </section>;
}
